/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
Container,
Box,
Typography,
Button,
MenuItem,
Card,
CardContent,
CardActions,
Divider,
Chip,
CircularProgress,
Grid,
useMediaQuery,
useTheme,
Slide,
Fade,
Zoom,
InputAdornment,
TextField,
Paper,
FormControl,
InputLabel,
Select,
IconButton,
Tooltip,
Accordion,
AccordionSummary,
AccordionDetails,
Avatar
} from '@mui/material';
import {
Add as AddIcon,
ArrowForward as ArrowForwardIcon,
School as SchoolIcon,
Search,
Refresh,
Person,
CalendarToday,
Edit as EditIcon,
Delete as DeleteIcon,
ExpandMore,
Science,
Factory,
Verified,
Description,
TrendingUp,
Business,
TramRounded
} from '@mui/icons-material';
import { APIProductDelete, APIProductList, APIResearcherList, APIUniversitiesList } from '../../utilities/APIS';
import { clearUserFilters, daysPassed, getIdCategory, getIdFullName, STORAGE_KEYS } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import { headers } from '../../utilities/Env';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';

const Products = ({ userOnly = false, userId = null }) => {
    const [productsData, setProductsData] = useState([]);
    const [researchersData, setResearchersData] = useState([]);
    const showToast = useCustomToast();
    const [universitiesData, setUniversitiesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [sortOrder, setSortOrder] = useState('newest');
    const [deletingProductId, setDeletingProductId] = useState(null);
    const [expandedProduct, setExpandedProduct] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Initialize state from sessionStorage or props
    const [isUserOnlyMode, setIsUserOnlyMode] = useState(() => {
        const stored = sessionStorage.getItem(STORAGE_KEYS.USER_ONLY);
        return stored ? JSON.parse(stored) : (userOnly || (location.state?.userOnly || false));
    });

    const [targetUserId, setTargetUserId] = useState(() => {
        const stored = sessionStorage.getItem(STORAGE_KEYS.USER_ID);
        return stored ? JSON.parse(stored) : (userId || (location.state?.userId || null));
    });

    // Update sessionStorage when state changes
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.USER_ONLY, JSON.stringify(isUserOnlyMode));
    }, [isUserOnlyMode]);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.USER_ID, JSON.stringify(targetUserId));
    }, [targetUserId]);

    // Also handle location state updates
    useEffect(() => {
        const locationState = location.state || {};
        if (locationState.userOnly !== undefined) {
            setIsUserOnlyMode(locationState.userOnly);
        }
        if (locationState.userId !== undefined) {
            setTargetUserId(locationState.userId);
        }
    }, [location.state]);

    useEffect(() => {
        fetchData();
    }, [isUserOnlyMode, targetUserId]);

    useEffect(() => {
        return () => {
            const currentPath = location.pathname;
            if (!currentPath.startsWith('/product-')) {
                clearUserFilters();
            }
        };
    }, [location.pathname]);

    useEffect(() => {
        filterAndSortData();
    }, [productsData, searchTerm, sortOrder, isUserOnlyMode, targetUserId]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [productsRes, unisRes, researchersRes] = await Promise.all([
                axios.get(APIProductList, { headers }),
                axios.get(APIUniversitiesList, { headers }),
                axios.get(APIResearcherList, { headers }),
            ]);

            let uni = Array.isArray(unisRes.data.data) ? unisRes.data.data.reverse() : [];
            setUniversitiesData(uni);

            let researchers = Array.isArray(researchersRes.data.data) ? researchersRes.data.data.reverse() : [];
            setResearchersData(researchers);

            let products = Array.isArray(productsRes.data.data) ? productsRes.data.data.reverse() : [];

            // Filter for user's products only if in user-only mode
            if (isUserOnlyMode && targetUserId) {
                products = products.filter(product =>
                    product.researcherName?.toString() === targetUserId.toString()
                );
            }
            setProductsData(products);
            setFilteredData(products);

        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleShowAllProducts = () => {
        clearUserFilters();
        setIsUserOnlyMode(false);
        setTargetUserId(null);
    };

    const getHeaderText = () => {
        if (isUserOnlyMode) {
            return "My Research Products";
        }
        return "Research Products";
    };

    const getSubheaderText = () => {
        if (isUserOnlyMode) {
            return "Manage and view your research products";
        }
        return "Explore innovative research products from universities worldwide";
    };

    const filterAndSortData = () => {
        let filtered = productsData;

        // Filter by search term - include new fields
        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.productTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getIdCategory(product.universityName, universitiesData)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getIdFullName(product.researcherName, researchersData)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.productDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.composition || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.manufacturer || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.tradeMark || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.patent || '')?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort data
        filtered = [...filtered].sort((a, b) => {
            switch (sortOrder) {
                case 'newest':
                    return new Date(b.uploadDate) - new Date(a.uploadDate);
                case 'oldest':
                    return new Date(a.uploadDate) - new Date(b.uploadDate);
                case 'title-asc':
                    return (a.productTitle || '').localeCompare(b.productTitle || '');
                case 'title-desc':
                    return (b.productTitle || '').localeCompare(a.productTitle || '');
                default:
                    return 0;
            }
        });

        setFilteredData(filtered);
    };

    const goToProductDetails = (product) => {
        const detailsProps = {
            productId: product.id,
            productTitle: product.productTitle,
            researcherName: product.researcherName,
            uploadDate: product.uploadDate,
            universityName: product.universityName,
            productDescription: product.productDescription,
            productDetails: product.productDetails,
            composition: product.composition,
            manufacturer: product.manufacturer,
            tradeMark: product.tradeMark,
            patent: product.patent
        };
        navigate("/product-details", { state: detailsProps });
    };

    const editProductDetails = (product) => {
        const detailsProps = {
            productId: product.id,
            productTitle: product.productTitle,
            researcherName: product.researcherName,
            uploadDate: product.uploadDate,
            universityName: product.universityName,
            productDescription: product.productDescription,
            productDetails: product.productDetails,
            composition: product.composition,
            manufacturer: product.manufacturer,
            tradeMark: product.tradeMark,
            patent: product.patent
        };
        navigate("/product-submit", { state: detailsProps });
    };

    const handleSubmitProduct = () => {
        navigate("/product-submit");
    };

    const handleDelete = async (product) => {
        const confirmDelete = window.confirm(
            `🚨 Delete Confirmation\n\n` +
            `Product: ${product.productTitle}\n` +
            `Researcher: ${getIdFullName(product.researcherName, researchersData)}\n` +
            `University: ${getIdCategory(product.universityName, universitiesData)}\n\n` +
            `This action cannot be undone. Are you sure you want to delete this product?`
        );

        if (!confirmDelete) {
            showToast("Cancelled", "Record deletion was cancelled", "info");
            return;
        }

        setDeletingProductId(product.id);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', product.id);

            const response = await axios.post(APIProductDelete, formDataToSend, { headers });

            if (response.data.status === "success") {
                showToast("Success", "Product deleted successfully!", "success");
                setProductsData(prev => prev.filter(p => p.id !== product.id));
                setFilteredData(prev => prev.filter(p => p.id !== product.id));
            } else {
                throw new Error(response.data.message || "Unknown server error");
            }
        } catch (error) {
            let errorMessage = "Failed to delete record. Please try again.";
            if (error.response) {
                errorMessage = error.response.data?.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            showToast("Error", errorMessage, "error");
        } finally {
            setDeletingProductId(null);
        }
    };

    const handleExpandClick = (productId) => {
        setExpandedProduct(expandedProduct === productId ? null : productId);
    };

    const hasAdditionalDetails = (product) => {
        return product.composition || product.manufacturer || product.tradeMark || product.patent;
    };

    const getProductDetailsCount = (product) => {
        let count = 0;
        if (product.composition) count++;
        if (product.manufacturer) count++;
        if (product.tradeMark) count++;
        if (product.patent) count++;
        return count;
    };

    const renderProductDetailsAccordion = (product) => {
        const isExpanded = expandedProduct === product.id;
        
        if (!hasAdditionalDetails(product)) {
            return null;
        }

        return (
            <Accordion 
                expanded={isExpanded} 
                onChange={() => handleExpandClick(product.id)}
                sx={{ 
                    mt: 2, 
                    boxShadow: 'none', 
                    border: `1px solid ${theme.palette.divider}`,
                    '&:before': { display: 'none' },
                    borderRadius: 1
                }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" color="secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Science fontSize="small" />
                        Technical Specifications ({getProductDetailsCount(product)} details)
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        {product.composition && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Science fontSize="small" />
                                        Composition
                                    </Typography>
                                    <Typography variant="body2">
                                        {product.composition}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        
                        {product.manufacturer && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Factory fontSize="small" />
                                        Manufacturer
                                    </Typography>
                                    <Typography variant="body2">
                                        {product.manufacturer}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        
                        {product.tradeMark && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TramRounded fontSize="small" />
                                        Trade Mark
                                    </Typography>
                                    <Typography variant="body2">
                                        {product.tradeMark}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        
                        {product.patent && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Verified fontSize="small" />
                                        Patent Information
                                    </Typography>
                                    <Typography variant="body2">
                                        {product.patent}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState/>
            </>
        );
    }

    return (
        <Box ssx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavigationMenu />

            <Container Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
                {/* Header Section */}
                <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                    <Box sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                        color: 'white',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {getHeaderText()}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                {getSubheaderText()}
                            </Typography>
                            {isUserOnlyMode && (
                                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                    Showing {productsData.length} of your products
                                </Typography>
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            sx={{
                                bgcolor: 'white',
                                color: theme.palette.secondary.main,
                                fontWeight: 'bold',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={handleSubmitProduct}
                        >
                            Submit Product
                        </Button>
                    </Box>
                </Slide>

                {/* Search and Filter Section */}
                {(productsData.length > 0 || searchTerm) && (
                    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    placeholder="Search products, researchers, universities, composition, manufacturer, trademark, patent..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search color="secondary" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                    ✕
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Sort By</InputLabel>
                                    <Select
                                        value={sortOrder}
                                        label="Sort By"
                                        onChange={(e) => setSortOrder(e.target.value)}
                                    >
                                        <MenuItem value="newest">Newest First</MenuItem>
                                        <MenuItem value="oldest">Oldest First</MenuItem>
                                        <MenuItem value="title-asc">Title A-Z</MenuItem>
                                        <MenuItem value="title-desc">Title Z-A</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <Button
                                    variant="outlined"
                                    onClick={fetchData}
                                    startIcon={<Refresh />}
                                    fullWidth
                                >
                                    Refresh
                                </Button>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                {isUserOnlyMode && (
                                    <Button
                                        variant="outlined"
                                        onClick={handleShowAllProducts}
                                        fullWidth
                                    >
                                        View All Products
                                    </Button>
                                )}
                            </Grid>
                        </Grid>

                        {/* Results Count */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" color="text.secondary">
                                Showing {filteredData.length} of {productsData.length} Products
                                {isUserOnlyMode && " (Your Products)"}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {searchTerm && (
                                    <Chip
                                        label={`Search: "${searchTerm}"`}
                                        onDelete={() => setSearchTerm('')}
                                        color="secondary"
                                        variant="outlined"
                                    />
                                )}
                                {isUserOnlyMode && (
                                    <Chip
                                        label="My Products Only"
                                        onDelete={() => navigate("/products")}
                                        color="success"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Products List */}
                <Box>
                    {filteredData.length === 0 ? (
                        <Grid item xs={12}>
                            <Fade in={true} timeout={800}>
                                <Paper sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        No products found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Try adjusting your search terms or filters
                                    </Typography>
                                </Paper>
                            </Fade>
                        </Grid>
                    ) : (
                        filteredData.map((product, index) => {
                            const isDeleting = deletingProductId === product.id;
                            const hasDetails = hasAdditionalDetails(product);
                            const productDetailsCount = getProductDetailsCount(product);

                            return (
                                <Grid item xs={12} key={product.id || index}>
                                    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                                        <Card elevation={2} sx={{
                                            borderLeft: `4px solid ${theme.palette.secondary.main}`,
                                            transition: 'transform 0.3s, box-shadow 0.3s',
                                            mb: 2,
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: theme.shadows[8]
                                            }
                                        }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    flexDirection: isMobile ? 'column' : 'row',
                                                    gap: 2,
                                                    mb: 2
                                                }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                                                            {product.productTitle}
                                                        </Typography>
                                                        <Typography variant="body1" color="text.secondary" paragraph>
                                                            {product.productDescription?.substring(0, 200)}
                                                            {product.productDescription?.length > 200 && '...'}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: isMobile ? 'flex-start' : 'flex-end',
                                                        gap: 1
                                                    }}>
                                                        <Chip
                                                            icon={<SchoolIcon />}
                                                            label={getIdCategory(product.universityName, universitiesData)}
                                                            color="secondary"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        {product.tradeMark && (
                                                            <Chip
                                                                icon={<TramRounded />}
                                                                label={product.tradeMark}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                        {isDeleting && (
                                                            <Chip
                                                                icon={<CircularProgress size={16} />}
                                                                label="Deleting..."
                                                                color="secondary"
                                                                size="small"
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Divider sx={{ my: 2 }} />

                                                {/* Product Metadata */}
                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Person color="secondary" sx={{ fontSize: 20 }} />
                                                            <Typography variant="body2">
                                                                <strong>Researcher:</strong> {getIdFullName(product.researcherName, researchersData)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CalendarToday color="secondary" sx={{ fontSize: 20 }} />
                                                            <Typography variant="body2">
                                                                <strong>Posted:</strong> {product.uploadDate}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Business color="secondary" sx={{ fontSize: 20 }} />
                                                            <Typography variant="body2">
                                                                <strong>Category:</strong> Research Product
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>

                                                {/* Additional Info */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 1
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Uploaded {daysPassed(product.uploadDate)} days ago
                                                    </Typography>
                                                    {hasDetails && (
                                                        <Tooltip title={`${productDetailsCount} technical detail(s) available`}>
                                                            <Chip
                                                                icon={<Science />}
                                                                label={`${productDetailsCount} Technical Detail${productDetailsCount > 1 ? 's' : ''}`}
                                                                size="small"
                                                                color="info"
                                                                variant="outlined"
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>

                                                {/* Expandable Details Section */}
                                                {renderProductDetailsAccordion(product)}
                                            </CardContent>

                                            <CardActions sx={{
                                                justifyContent: 'space-between',
                                                p: 2,
                                                pt: 0,
                                                borderTop: `1px solid ${theme.palette.divider}`
                                            }}>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        icon={<TrendingUp />}
                                                        label="Research Product"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    {product.manufacturer && (
                                                        <Chip
                                                            icon={<Factory />}
                                                            label={product.manufacturer.length > 30 ? product.manufacturer.substring(0, 30) + '...' : product.manufacturer}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>

                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {/* Edit Button - Only show for user's own products in user-only mode */}
                                                    {isUserOnlyMode && (
                                                        <>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                endIcon={<EditIcon />}
                                                                onClick={() => editProductDetails(product)}
                                                                size="small"
                                                                disabled={isDeleting}
                                                                sx={{
                                                                    backgroundColor: theme.palette.success.main,
                                                                    color: 'white',
                                                                    '&:hover': {
                                                                        backgroundColor: theme.palette.secondary.main,
                                                                    }
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                        
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                endIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                                                                onClick={() => handleDelete(product)}
                                                                size="small"
                                                                disabled={isDeleting}
                                                            >
                                                                {isDeleting ? 'Deleting...' : 'Delete'}
                                                            </Button>
                                                        </>
                                                    )}

                                                    <Button
                                                        variant="contained"
                                                        endIcon={<ArrowForwardIcon />}
                                                        onClick={() => goToProductDetails(product)}
                                                        size="small"
                                                        disabled={isDeleting}
                                                    >
                                                        View Details
                                                    </Button>
                                                </Box>
                                            </CardActions>
                                        </Card>
                                    </Zoom>
                                </Grid>
                            );
                        })
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default Products;